import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';           // ← Obbligatorio per emoji-mart v5+

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Loader2, Gift, Shield, AlertCircle, Zap, Pencil } from 'lucide-react';

import { reactionRoleApi, giveawayApi, pluginApi, serverApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';

// Custom Commands Content
export function CommandsContent({ serverId }: { serverId: string }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold">Custom Commands</h3>
        <Button>
          <Plus className="w-4 h-4 mr-2" /> Add Command
        </Button>
      </div>
      <div className="glass-card p-6">
        <p className="text-muted-foreground text-center py-8">
          No custom commands configured yet
        </p>
      </div>
      {/* Add form, list, etc. later */}
    </div>
  );
}

// =============================================
// REACTION ROLES CONTENT (COMPLETAMENTE FIXATO)
// =============================================
export function ReactionRolesContent({ serverId }: { serverId: string }) {
  useQueryClient();

  // Fetch reaction roles
  const { data: reactionRolesRaw, isLoading: loadingRR } = useQuery({
    queryKey: ['reaction-roles', serverId],
    queryFn: () => reactionRoleApi.getReactionRoles(serverId),
  });
  const reactionRoles = Array.isArray(reactionRolesRaw) ? reactionRolesRaw : [];

  // Fetch ruoli del server per la select
  // Usa pluginApi.getRoles(serverId) per popolare il menu ruoli
  const { data: roles = [], isLoading: loadingRoles, error: rolesError } = useQuery({
    queryKey: ['roles', serverId],
    queryFn: () => serverApi.getRoles(serverId),
  });

  const { data: channels = [], isLoading: loadingChannels } = useQuery({
    queryKey: ['channels', serverId],
    queryFn: () => serverApi.getChannels(serverId),
  });

  const { mutate: createReactionRole } = useApiMutation(
    (payload: any) => reactionRoleApi.createReactionRole(serverId, payload),
    { invalidateQueries: [['reaction-roles', serverId]] }
  );

  const { mutate: updateReactionRole } = useApiMutation(
    ({ id, payload }: { id: string; payload: any }) => reactionRoleApi.updateReactionRole(serverId, id, payload),
    { invalidateQueries: [['reaction-roles', serverId]] }
  );

  const { mutate: deleteReactionRole } = useApiMutation(
    (id: string) => reactionRoleApi.deleteReactionRole(serverId, id),
    { invalidateQueries: [['reaction-roles', serverId]] }
  );

  const [showForm, setShowForm] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [currentIdx, setCurrentIdx] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    messageId: '',
    channelId: '',
    messageText: '',
    reactions: [{ emoji: '', roleId: '' }] as { emoji: string; roleId: string }[],
  });

  const addReaction = () => {
    setFormData(prev => ({
      ...prev,
      reactions: [...prev.reactions, { emoji: '', roleId: '' }],
    }));
  };

  const removeReaction = (index: number) => {
    setFormData(prev => ({
      ...prev,
      reactions: prev.reactions.filter((_, i) => i !== index),
    }));
  };

  const updateReaction = (index: number, field: 'emoji' | 'roleId', value: string) => {
    setFormData(prev => ({
      ...prev,
      reactions: prev.reactions.map((r, i) =>
        i === index ? { ...r, [field]: value } : r
      ),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validazione robusta
    const validReactions = formData.reactions.filter(r => r.emoji?.trim() && r.roleId?.trim());

    if (!formData.channelId?.trim()) {
      alert('Inserisci un Channel ID valido!');
      return;
    }
    if (validReactions.length === 0) {
      alert('Devi aggiungere almeno una reazione con emoji e ruolo!');
      return;
    }

    const payload = {
      channelId: formData.channelId.trim(),
      messageId: null,
      messageContent: formData.messageText?.trim() || null,
      reactions: validReactions,
      enabled: true
    };
    console.log('Payload che sto inviando:', JSON.stringify(payload, null, 2));

    const reset = () => {
      setShowForm(false);
      setEditingRoleId(null);
      setFormData({ messageId: '', channelId: '', messageText: '', reactions: [{ emoji: '', roleId: '' }] });
    };

    if (editingRoleId) {
      updateReactionRole(
        { id: editingRoleId, payload },
        {
          onSuccess: () => {
            alert('Reaction role aggiornata con successo!');
            reset();
          },
          onError: (err: any) => {
            console.error('Errore API:', err);
            const errorMsg = err?.response?.data?.message || err?.message || 'Errore sconosciuto';
            alert(`Errore durante l\'aggiornamento: ${errorMsg}`);
          },
        }
      );
      return;
    }

    createReactionRole(payload, {
      onSuccess: () => {
        alert('Reaction role creata con successo!');
        reset();
      },
      onError: (err: any) => {
        console.error('Errore API:', err);
        const errorMsg = err?.response?.data?.message || err?.message || 'Errore sconosciuto';
        alert(`Errore durante la creazione: ${errorMsg}`);
      },
    });
  };

  const handleEdit = (rr: any) => {
    const normalizedReactions = Array.isArray(rr.reactions)
      ? rr.reactions
      : rr.reactions && typeof rr.reactions === 'object'
        ? Object.entries(rr.reactions).map(([emoji, roleId]) => ({ emoji, roleId }))
        : [];

    setEditingRoleId(rr.id);
    setShowForm(true);
    setFormData({
      messageId: rr.messageId || '',
      channelId: rr.channelId || '',
      messageText: rr.messageContent || rr.messageText || '',
      reactions: normalizedReactions.length > 0 ? normalizedReactions : [{ emoji: '', roleId: '' }],
    });
  };

  if (loadingRR || loadingRoles || loadingChannels) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }
    if (rolesError) {
      return <div className="text-center text-destructive py-8">Errore nel caricamento dei ruoli. Controlla che il bot sia nel server e abbia i permessi.</div>;
    }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold">Reaction Roles</h3>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="w-4 h-4 mr-2" /> Crea Nuovo
        </Button>
      </div>

      {/* ==================== FORM CREAZIONE ==================== */}
      {showForm && (
        <form onSubmit={handleSubmit} className="glass-card p-8 space-y-6 border-2 border-primary/20">
          <div className="flex items-center gap-3 pb-4 border-b">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-2xl">🎭</span>
            </div>
            <h4 className="text-lg font-semibold">Nuova Reaction Role</h4>
          </div>

          <div>
            <Label>Channel ID</Label>
            <Select
              value={formData.channelId || 'none'}
              onValueChange={(value) => setFormData({ ...formData, channelId: value === 'none' ? '' : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleziona canale..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Seleziona canale...</SelectItem>
                {(channels as any[]).map((channel: any) => (
                  <SelectItem key={channel.id} value={channel.id}>
                    #{channel.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Messaggio (opzionale)</Label>
            <Textarea
              value={formData.messageText}
              onChange={e => setFormData({ ...formData, messageText: e.target.value })}
              placeholder="Il bot invierà questo messaggio..."
            />
          </div>

          <div className="space-y-4">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Zap className="w-5 h-5" /> Reazioni → Ruoli
            </Label>

            {formData.reactions.map((r, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-muted/30 p-4 rounded-lg relative">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => { setCurrentIdx(idx); setEmojiPickerOpen(true); }}
                >
                  {r.emoji ? <span className="text-3xl">{r.emoji}</span> : <span className="text-2xl text-muted-foreground">+</span>}
                </Button>

                {emojiPickerOpen && currentIdx === idx && (
                  <div className="absolute z-50 top-14 left-0">
                    <Picker
                      data={data}
                      onEmojiSelect={(emoji: any) => {
                        updateReaction(idx, 'emoji', emoji.native);
                        setEmojiPickerOpen(false);
                      }}
                      theme="dark"
                      previewPosition="none"
                      maxFrequentRows={0}
                    />
                  </div>
                )}

                <select
                  className="flex-1 border rounded px-3 py-2 bg-background"
                  value={r.roleId}
                  onChange={e => updateReaction(idx, 'roleId', e.target.value)}
                  required
                >
                  <option value="">Seleziona ruolo...</option>
                  {rolesError && (
                    <option value="" disabled>
                      Errore nel caricamento dei ruoli
                    </option>
                  )}
                  {!rolesError && roles.length === 0 && !loadingRoles && (
                    <option value="" disabled>
                      Nessun ruolo disponibile
                    </option>
                  )}
                  {roles.map((role: any) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeReaction(idx)}
                  disabled={formData.reactions.length === 1}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}

            <Button type="button" variant="secondary" onClick={addReaction}>
              + Aggiungi Reazione
            </Button>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit">{editingRoleId ? 'Salva Modifiche' : 'Crea Reaction Role'}</Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowForm(false);
                setEditingRoleId(null);
                setFormData({ messageId: '', channelId: '', messageText: '', reactions: [{ emoji: '', roleId: '' }] });
              }}
            >
              Annulla
            </Button>
          </div>
        </form>
      )}

      {/* ==================== LISTA ESISTENTI ==================== */}
      <div className="space-y-4">
        {reactionRoles.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-muted/50 mb-4">
              <span className="text-5xl">🎭</span>
            </div>
            <h4 className="text-lg font-semibold mb-2">Nessuna Reaction Role</h4>
            <p className="text-muted-foreground max-w-md mx-auto mb-6">
              Crea la prima per assegnare ruoli automaticamente con le reazioni
            </p>
            <Button onClick={() => setShowForm(true)} variant="outline" size="lg">
              <Plus className="w-4 h-4 mr-2" /> Inizia Ora
            </Button>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground px-1">
              {reactionRoles.length} reaction role configurate
            </p>
            {reactionRoles.map((rr: any) => (
              <div key={rr.id} className="glass-card p-6 hover:shadow-lg transition-shadow border-l-4 border-l-primary">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-semibold">Messaggio: <code className="text-xs">{rr.messageId || 'Auto-generated'}</code></p>
                    <p className="text-sm text-muted-foreground">
                      Channel: <code>{(channels as any[]).find((c: any) => c.id === rr.channelId)?.name ? `#${(channels as any[]).find((c: any) => c.id === rr.channelId)?.name}` : rr.channelId}</code>
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="hover:bg-primary/10"
                      onClick={() => handleEdit(rr)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:bg-destructive/10"
                      onClick={() => confirm('Eliminare questa reaction role?') && deleteReactionRole(rr.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="mt-4">
                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Zap className="w-4 h-4" /> Mapping Reazioni
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {rr.reactions?.map((r: any, i: number) => (
                      <div key={i} className="flex items-center gap-2 bg-muted px-4 py-2 rounded-lg">
                        <span className="text-2xl">{r.emoji}</span>
                        <span className="text-muted-foreground">→</span>
                        <span className="font-mono text-xs">{r.roleId.slice(0, 10)}...</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// =============================================
// GIVEAWAY CONTENT (FIXATO)
// =============================================
export function GiveawayContent({ serverId }: { serverId: string }) {
  const { data: giveawaysRaw, isLoading } = useQuery({
    queryKey: ['servers', serverId, 'giveaways'],
    queryFn: () => giveawayApi.getGiveaways(serverId),
  });
  const giveaways = Array.isArray(giveawaysRaw) ? giveawaysRaw : [];

  const { mutate: createGiveaway, isPending: creating } = useApiMutation(
    (data: any) => giveawayApi.createGiveaway(serverId, data),
    { invalidateQueries: [['servers', serverId, 'giveaways']] }
  );

  const { mutate: endGiveaway, isPending: ending } = useApiMutation(
    (id: string) => giveawayApi.endGiveaway(serverId, id),
    { invalidateQueries: [['servers', serverId, 'giveaways']] }
  );

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    prize: '',
    description: '',
    winners: 1,
    endTime: '',
    channelId: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createGiveaway(formData);
    setFormData({ title: '', prize: '', description: '', winners: 1, endTime: '', channelId: '' });
    setShowForm(false);
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold">Giveaways</h3>
        <Button onClick={() => setShowForm(!showForm)}>
          <Plus className="w-4 h-4 mr-2" /> Crea Giveaway
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="glass-card p-6 space-y-4">
          <div>
            <Label>Titolo</Label>
            <Input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} required />
          </div>
          <div>
            <Label>Premio</Label>
            <Input value={formData.prize} onChange={e => setFormData({ ...formData, prize: e.target.value })} required />
          </div>
          <div>
            <Label>Descrizione</Label>
            <Textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Numero Vincitori</Label>
              <Input type="number" min="1" value={formData.winners} onChange={e => setFormData({ ...formData, winners: parseInt(e.target.value) })} />
            </div>
            <div>
              <Label>Data Fine</Label>
              <Input type="datetime-local" value={formData.endTime} onChange={e => setFormData({ ...formData, endTime: e.target.value })} required />
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={creating}>
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crea Giveaway'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Annulla</Button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {giveaways.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">Nessun giveaway attivo</p>
        ) : (
          giveaways.map((g: any) => (
            <div key={g.id} className="glass-card p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-medium flex items-center gap-2">
                    <Gift className="w-4 h-4" /> {g.title}
                  </p>
                  <p className="text-sm text-muted-foreground">Premio: {g.prize}</p>
                  {g.description && <p className="text-sm text-muted-foreground">{g.description}</p>}
                  <p className="text-sm text-muted-foreground">
                    {g.winners} vincitore{g.winners > 1 ? 'i' : ''} • Fine: {new Date(g.endTime).toLocaleString()}
                  </p>
                </div>
                {!g.ended && (
                  <Button variant="outline" size="sm" onClick={() => endGiveaway(g.id)} disabled={ending}>
                    Termina Ora
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// =============================================
// MODERATION CONTENT
// =============================================
export function ModerationContent({ serverId }: { serverId: string }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold">Moderation</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm"><Shield className="w-4 h-4 mr-2" /> Logs</Button>
          <Button variant="outline" size="sm"><AlertCircle className="w-4 h-4 mr-2" /> Warnings</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="glass-card p-4 text-center"><p className="text-2xl font-bold">0</p><p className="text-sm text-muted-foreground">Bans</p></div>
        <div className="glass-card p-4 text-center"><p className="text-2xl font-bold">0</p><p className="text-sm text-muted-foreground">Kicks</p></div>
        <div className="glass-card p-4 text-center"><p className="text-2xl font-bold">0</p><p className="text-sm text-muted-foreground">Warnings</p></div>
      </div>

      <div className="glass-card p-6">
        <h4 className="font-semibold mb-4">Azioni Recenti</h4>
        <p className="text-muted-foreground text-center py-8">Nessuna azione recente</p>
      </div>
    </div>
  );
}

// =============================================
// LEVELING CONTENT (pulito)
// =============================================
export function LevelingContent({ serverId }: { serverId: string }) {
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    xpPerMessage: 15,
    levelUpMessage: 'GG {user}, hai raggiunto il livello {level}!',
  });

  // TODO: sostituisci con useQuery reale
  const leaderboard: any[] = [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold">Sistema di Livelli</h3>
        <Button onClick={() => setShowSettings(!showSettings)} variant="outline">
          Impostazioni
        </Button>
      </div>

      {showSettings && (
        <div className="glass-card p-6 space-y-4">
          <div>
            <Label>XP per messaggio</Label>
            <Input
              type="number"
              value={settings.xpPerMessage}
              onChange={e => setSettings({ ...settings, xpPerMessage: parseInt(e.target.value) })}
              min="1"
            />
          </div>
          <div>
            <Label>Messaggio Livello Up</Label>
            <Textarea
              value={settings.levelUpMessage}
              onChange={e => setSettings({ ...settings, levelUpMessage: e.target.value })}
            />
          </div>
          <Button>Salva Impostazioni</Button>
        </div>
      )}

      <div className="glass-card p-6">
        <h4 className="font-semibold mb-4">Classifica Top</h4>
        <div className="space-y-3">
          {leaderboard.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Nessun dato ancora</p>
          ) : (
            leaderboard.slice(0, 10).map((entry, index) => (
              <div key={entry.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-3">
                  <span className="font-bold text-lg text-primary">#{index + 1}</span>
                  <div>
                    <p className="font-medium">{entry.user?.username}</p>
                    <p className="text-sm text-muted-foreground">Livello {entry.level}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}